import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import type { DepotCheckResponse, DepotOption } from '@ctp/shared';
import { Spinner } from './ui';

/**
 * Leerdepot-Zelle (EPIC I): Dropdown aller Leerdepots; eine Auswahl startet sofort die Avis-Prüfung.
 * Das Feld färbt sich nach Ergebnis — grün = angemeldet/verfügbar, rot = nicht angemeldet/Fehler.
 * I2b: Depots mit `requiresReference` (IGS/HCS/MedRepair) blenden ein Referenzfeld ein — die Prüfung
 * startet erst mit Enter/Verlassen des Felds. I2c: Depots mit `integrationType 'none'` sind „nicht
 * prüfbar" (neutral, keine Prüfung). Wird in der Standard- UND der Premium-Tabelle genutzt; bei
 * Premium-Zeilen persistiert die Auswahl serverseitig (P7-Recheck), hier ist die Zelle nur Anzeige.
 */
export function DepotCell({
  depots,
  selectedId,
  result,
  checking,
  onSelect,
}: {
  depots: DepotOption[];
  selectedId: string | undefined;
  result: DepotCheckResponse | undefined;
  checking: boolean;
  onSelect: (depotId: string, reference?: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [reference, setReference] = useState('');
  const selected = depots.find((d) => d.id === selectedId);
  const requiresReference = !!selected?.requiresReference;
  const notCheckable = selected?.integrationType === 'none';

  const outcome = checking ? 'checking' : result?.outcome;
  const tone = notCheckable
    ? 'border-input bg-muted text-muted-foreground'
    : outcome === 'available'
      ? 'border-status-ok/40 bg-status-ok/15 text-status-ok'
      : outcome === 'not_available' || outcome === 'not_found' || outcome === 'error'
        ? 'border-status-problem/40 bg-status-problem/15 text-status-problem'
        : 'border-input bg-background text-foreground';
  // Bekannte Fehlergründe übersetzen (i18n DE/EN); sonst Servermeldung als Detail.
  const codeText =
    result?.code === 'reference_required'
      ? t('dashboard.depot.referenceRequired')
      : result?.code === 'credentials_missing'
        ? t('dashboard.depot.credentialsMissing')
        : result?.code === 'not_checkable'
          ? t('dashboard.depot.notCheckable')
          : undefined;
  const title = notCheckable
    ? t('dashboard.depot.notCheckable')
    : result?.outcome === 'error'
      ? (codeText ?? result.message ?? t('dashboard.depot.error'))
      : result?.outcome === 'available'
        ? t('dashboard.depot.available')
        : result?.outcome === 'not_available' || result?.outcome === 'not_found'
          ? t('dashboard.depot.notAvailable')
          : requiresReference
            ? t('dashboard.depot.referenceHint')
            : undefined;

  const submitReference = (): void => {
    if (selectedId && reference.trim().length > 0) onSelect(selectedId, reference.trim());
  };

  return (
    <div
      className={`relative inline-flex items-center gap-1 rounded-md border text-xs font-medium ${tone}`}
      title={title}
    >
      <select
        className="appearance-none bg-transparent py-1 pl-2 pr-6 focus:outline-none"
        value={selectedId ?? ''}
        disabled={checking}
        onChange={(e) => {
          setReference('');
          onSelect(e.target.value);
        }}
        // Zeilen-Klick (Detailansicht) nicht auslösen, wenn man das Dropdown bedient.
        onClick={(e) => e.stopPropagation()}
      >
        <option value="">{t('dashboard.depot.select')}</option>
        {depots.map((d) => (
          <option key={d.id} value={d.id}>
            {d.name}
          </option>
        ))}
      </select>
      {requiresReference && !notCheckable ? (
        <input
          type="text"
          className="w-28 rounded border border-input bg-background px-1.5 py-0.5 text-xs text-foreground focus:outline-none"
          placeholder={t('dashboard.depot.referencePlaceholder')}
          value={reference}
          disabled={checking}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setReference(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submitReference();
          }}
          onBlur={() => {
            // Nur prüfen, wenn eine Referenz eingegeben wurde und noch kein Ergebnis dazu vorliegt.
            if (reference.trim().length > 0 && !checking && result?.outcome === undefined)
              submitReference();
          }}
        />
      ) : null}
      {notCheckable ? (
        <span className="pr-5 text-[11px]">{t('dashboard.depot.notCheckable')}</span>
      ) : null}
      <span className="pointer-events-none absolute right-1.5 flex items-center">
        {checking ? <Spinner /> : <ChevronDown className="h-3.5 w-3.5 opacity-60" />}
      </span>
    </div>
  );
}
